<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import TurnstileError from '@/components/base/TurnstileError.vue'
import { useTurnstile } from '@/composables/useTurnstile'

const props = withDefaults(
  defineProps<{
    siteKey?: string
    theme?: 'auto' | 'light' | 'dark'
    appearance?: 'always' | 'execute' | 'interaction-only'
    retry?: 'auto' | 'never'
    refreshExpired?: 'auto' | 'manual'
    tabindex?: number
    // If true, render immediately on mount without waiting for IntersectionObserver
    renderOnMount?: boolean
  }>(),
  {
    theme: 'auto',
    appearance: 'always',
    retry: 'auto',
    refreshExpired: 'auto',
    tabindex: 0,
    renderOnMount: false
  }
)

const emit = defineEmits<{
  (e: 'token', token: string): void
  (e: 'expired'): void
  (e: 'error', err?: any): void
  (e: 'rendered', widgetId: string): void
}>()

const containerId = `turnstile-${Math.random().toString(36).slice(2)}`
const isRendered = ref(false)
const widgetId = ref<string | null>(null)
const errored = ref(false)
let observer: IntersectionObserver | null = null

const { renderOnce, reset, destroy } = useTurnstile()

async function doRender() {
  if (isRendered.value) return
  try {
    // ensure DOM element is present
    await nextTick()
    const { widgetId: wid } = await renderOnce(containerId, props.siteKey, {
      theme: props.theme,
      appearance: props.appearance,
      retry: props.retry,
      'refresh-expired': props.refreshExpired,
      tabindex: props.tabindex,
      callback: (token: string) => {
        errored.value = false
        emit('token', token)
      },
      'expired-callback': () => emit('expired'),
      'error-callback': () => {
        errored.value = true
        emit('error')
      }
    })
    if (wid) {
      widgetId.value = wid
      isRendered.value = true
      emit('rendered', wid)
    }
  } catch (e) {
    errored.value = true
    emit('error', e)
  }
}

async function retryRender() {
  errored.value = false
  isRendered.value = false
  widgetId.value = null
  destroy(containerId)
  await doRender()
}

onMounted(() => {
  // If IntersectionObserver is available and renderOnMount is false, wait for visibility
  if (!props.renderOnMount && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          doRender()
          if (observer) {
            observer.disconnect()
            observer = null
          }
        }
      })
    }, { root: null, threshold: 0 })
    const el = document.getElementById(containerId)
    if (el) {
      observer.observe(el)
    } else {
      // element might not be ready yet
      setTimeout(() => {
        const lateEl = document.getElementById(containerId)
        if (lateEl && observer) observer.observe(lateEl)
      }, 0)
    }
  } else {
    // Fallback: render on mount (Stage2-mounted lazy-load)
    doRender()
  }
})

onBeforeUnmount(() => {
  // Try to reset existing widget (if API supports it), then destroy DOM and internal map
  if (widgetId.value) {
    try {
      reset(widgetId.value)
    } catch {
      // ignore
    }
  }
  destroy(containerId)
  if (observer) observer.disconnect()
})

// In case siteKey changes after mount (rare), re-render
watch(
  () => props.siteKey,
  (newKey, oldKey) => {
    if (newKey !== oldKey) {
      // full re-render
      isRendered.value = false
      widgetId.value = null
      destroy(containerId)
      doRender()
    }
  }
)
</script>

<template>
  <div class="turnstile-wrap">
    <div :id="containerId" class="cf-turnstile" />
    <TurnstileError v-if="errored" @retry="retryRender" />
  </div>
</template>

<style scoped>
.turnstile-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
</style>
